import React, { useState, useEffect } from 'react';
import { 
  DeliveryTierFormProps, 
  DeliveryTier, 
  DublinLocation,
  DUBLIN_LOCATIONS,
  searchLocations,
  getUnassignedLocations
} from '../../../types';
import { Button, Input } from '../../atoms';
import LocationSelector from './LocationSelector';
import './DeliveryTierForm.css';

const DeliveryTierForm: React.FC<DeliveryTierFormProps> = ({
  tier,
  onSubmit,
  onCancel,
  loading = false
}) => {
  const [formData, setFormData] = useState({
    name: '',
    price: 0,
    isActive: true
  });
  const [selectedLocations, setSelectedLocations] = useState<DublinLocation[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (tier) {
      setFormData({
        name: tier.name,
        price: tier.price,
        isActive: tier.isActive
      });
      setSelectedLocations(tier.locations);
    }
  }, [tier]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Tier name is required';
    }

    if (formData.price < 0) {
      newErrors.price = 'Price must be 0 or greater';
    }

    if (selectedLocations.length === 0) {
      newErrors.locations = 'At least one location must be selected';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    onSubmit({
      ...formData,
      locations: selectedLocations
    });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = 'checked' in e.target ? e.target.checked : false;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (type === 'number' ? parseFloat(value) || 0 : value)
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleLocationsChange = (locations: DublinLocation[]) => {
    setSelectedLocations(locations);
    
    // Clear error when locations are selected
    if (errors.locations) {
      setErrors(prev => ({
        ...prev,
        locations: ''
      }));
    }
  };

  const isEditing = !!tier;

  return (
    <div className="delivery-tier-form">
      <div className="form-header">
        <h3>{isEditing ? 'Edit Delivery Tier' : 'Create New Delivery Tier'}</h3>
        <p>
          {isEditing 
            ? 'Update the delivery tier details and assigned locations.' 
            : 'Create a new delivery pricing tier and assign Dublin locations to it.'
          }
        </p>
      </div>

      <form onSubmit={handleSubmit} className="tier-form">
        <div className="form-group">
          <Input
            id="name"
            name="name"
            label="Tier Name"
            type="text"
            value={formData.name}
            onChange={handleInputChange}
            placeholder="e.g., City Center, North Dublin, etc."
            required
            error={errors.name}
            disabled={loading}
          />
        </div>

        <div className="form-group">
          <Input
            id="price"
            name="price"
            label="Delivery Price (€)"
            type="number"
            value={formData.price}
            onChange={handleInputChange}
            placeholder="0.00"
            min={0}
            step={0.01}
            required
            error={errors.price}
            disabled={loading}
          />
        </div>

        <div className="form-group">
          <div className="checkbox-group">
            <input
              type="checkbox"
              id="isActive"
              name="isActive"
              checked={formData.isActive}
              onChange={handleInputChange}
              disabled={loading}
            />
            <label htmlFor="isActive">Active (tier is available for use)</label>
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Assigned Locations</label>
          <LocationSelector
            selectedLocations={selectedLocations}
            onLocationsChange={handleLocationsChange}
          />
          {errors.locations && (
            <span className="error-message">{errors.locations}</span>
          )}
          <div className="location-summary">
            {selectedLocations.length > 0 && (
              <p className="selected-count">
                {selectedLocations.length} location{selectedLocations.length !== 1 ? 's' : ''} selected
              </p>
            )}
          </div>
        </div>

        <div className="form-actions">
          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={loading}
          >
            {loading ? 'Saving...' : (isEditing ? 'Update Tier' : 'Create Tier')}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default DeliveryTierForm;
